import { V2_ENDPOINT } from "@/consts";
import { authenticatedFetch } from "@/lib/authenticatedFetch";

const SPREADSHEET_ID_RE = /\/spreadsheets\/d\/([a-zA-Z0-9_-]+)/;
const GID_RE = /[?&#]gid=(\d+)/;
const BARE_ID_RE = /^[a-zA-Z0-9_-]{10,}$/;

export interface ParsedGoogleSheet {
  spreadsheetId: string;
  worksheetId: number;
  isUrl: boolean;
}

export function parseGoogleSheetInput(input: string): ParsedGoogleSheet | null {
  const trimmed = input.trim();
  if (!trimmed) return null;

  const match = trimmed.match(SPREADSHEET_ID_RE);
  if (match) {
    const gidMatch = trimmed.match(GID_RE);
    return {
      spreadsheetId: match[1],
      worksheetId: gidMatch ? parseInt(gidMatch[1], 10) : 0,
      isUrl: true,
    };
  }

  if (BARE_ID_RE.test(trimmed)) {
    return { spreadsheetId: trimmed, worksheetId: 0, isUrl: false };
  }

  return null;
}

export interface WorksheetTab {
  sheetId: number;
  title: string;
  index: number;
}

export type WorksheetLookup =
  | { status: "found"; spreadsheetTitle: string | null; sheets: WorksheetTab[] }
  | { status: "not_found" }
  | { status: "error"; message: string };

const CACHE_TTL_MS = 5 * 60 * 1000;
const cache = new Map<string, { result: WorksheetLookup; ts: number }>();

export async function fetchWorksheetTabs(
  connectorId: string,
  connectorType: string,
  spreadsheetIdOrUrl: string,
): Promise<WorksheetLookup> {
  const cacheKey = `${connectorId}:${spreadsheetIdOrUrl}`;
  const cached = cache.get(cacheKey);
  if (cached) {
    if (Date.now() - cached.ts < CACHE_TTL_MS) return cached.result;
    cache.delete(cacheKey);
  }

  let response: Response;
  try {
    response = await authenticatedFetch(`${V2_ENDPOINT}/v1/actions/google-sheets-list-worksheets/test`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        inputs: { url: spreadsheetIdOrUrl },
        connector: { id: connectorId, type: connectorType },
      }),
    });
  } catch (err) {
    return { status: "error", message: err instanceof Error ? err.message : "Network error" };
  }

  if (!response.ok) {
    if (response.status === 404) return { status: "not_found" };
    return { status: "error", message: `HTTP ${response.status}` };
  }

  let data: unknown;
  try {
    data = await response.json();
  } catch {
    return { status: "error", message: "Malformed response" };
  }

  const result = (
    data as {
      data?: {
        result?: {
          success?: boolean;
          data?: { spreadsheetTitle?: string; sheets?: unknown[] };
        };
      };
    }
  )?.data?.result;

  if (!result?.success) {
    return { status: "not_found" };
  }

  const sheets = result.data?.sheets;
  if (!Array.isArray(sheets)) {
    return { status: "not_found" };
  }

  const found: WorksheetLookup = {
    status: "found",
    spreadsheetTitle: typeof result.data?.spreadsheetTitle === "string" ? result.data.spreadsheetTitle : null,
    sheets: (sheets as Record<string, unknown>[]).map(s => ({
      sheetId: typeof s.sheetId === "number" ? s.sheetId : 0,
      title: typeof s.title === "string" ? s.title : "Untitled",
      index: typeof s.index === "number" ? s.index : 0,
    })),
  };

  cache.set(cacheKey, { result: found, ts: Date.now() });
  return found;
}
